import moment from "moment";

let jsPDF;
try {
	jsPDF = require("jspdf").default || require("jspdf");
} catch (error) {
	console.error("jsPDF non è installato. Esegui: npm install jspdf");
	jsPDF = null;
}

let html2canvas;
try {
	html2canvas = require("html2canvas");
} catch (error) {
	console.error(
		"html2canvas non è installato. Esegui: npm install html2canvas",
	);
	html2canvas = null;
}

/**
 * Genera un PDF con i radar chart di comparazione giocatori
 * @param {Array} selectedPlayers - Array dei giocatori selezionati
 * @param {Object} playersData - Dati dei giocatori
 * @param {Object} valuationsDataMap - Mappa delle valutazioni per giocatore
 * @param {Array} combinedRadarData - Dati per il radar chart combinato
 * @param {Function} t - Funzione di traduzione
 */
export const generateComparisonPDF = async (
	selectedPlayers,
	playersData,
	valuationsDataMap,
	combinedRadarData,
	t,
) => {
	if (!jsPDF) {
		throw new Error(
			"jsPDF non è installato. Per utilizzare questa funzionalità, installa jsPDF eseguendo: npm install jspdf",
		);
	}

	if (!html2canvas) {
		throw new Error(
			"html2canvas non è installato. Per utilizzare questa funzionalità, installa html2canvas eseguendo: npm install html2canvas",
		);
	}

	// =========================
	// Theme + Spacing (premium)
	// =========================
	const theme = {
		primary: [16, 89, 191],
		primaryDark: [9, 53, 128],
		primaryLight: [59, 130, 246],
		accent: [28, 193, 255], // usata come highlight
		bg: [245, 247, 250],
		border: [229, 231, 235],
		text: [30, 41, 59],
		muted: [100, 116, 139],
	};

	const SP = {
		pagePad: 42,
		blockGap: 12,
		cardPad: 12,
		footerH: 48,
	};

	// =========================
	// Document setup (A4, pt)
	// =========================
	const doc = new jsPDF({unit: "pt", format: "a4"});

	const safeText = (v, fallback = "") =>
		v === null || v === undefined || v === "" ? fallback : String(v);

	const setOpacity = (opacity) => {
		try {
			if (doc.GState) doc.setGState(new doc.GState({opacity}));
		} catch (_) {
			/* no-op */
		}
	};

	const resetOpacity = () => {
		try {
			if (doc.GState) doc.setGState(new doc.GState({opacity: 1}));
		} catch (_) {
			/* no-op */
		}
	};

	const ellipsize = (
		text,
		maxWidth,
		fontSize = 11,
		font = ["helvetica", "normal"],
	) => {
		const s = safeText(text, "");
		if (!s) return "";
		doc.setFont(font[0], font[1]);
		doc.setFontSize(fontSize);
		if (doc.getTextWidth(s) <= maxWidth) return s;

		const ell = "…";
		let lo = 0;
		let hi = s.length;
		while (lo < hi) {
			const mid = Math.floor((lo + hi) / 2);
			const candidate = s.slice(0, mid) + ell;
			if (doc.getTextWidth(candidate) <= maxWidth) lo = mid + 1;
			else hi = mid;
		}
		return s.slice(0, Math.max(0, lo - 1)) + ell;
	};

	const drawCard = (x, y, w, h, {radius = 12, shadow = true} = {}) => {
		if (shadow) {
			doc.setFillColor(0, 0, 0);
			setOpacity(0.06);
			doc.roundedRect(x + 2, y + 3, w, h, radius, radius, "F");
			resetOpacity();
		}
		doc.setDrawColor(...theme.border);
		doc.setLineWidth(0.8);
		doc.setFillColor(255, 255, 255);
		doc.roundedRect(x, y, w, h, radius, radius, "FD");
	};

	const drawHeader = (pageTitle, {landscape = false} = {}) => {
		const pageWidth = doc.internal.pageSize.getWidth();
		const headerH = 86;

		// background
		doc.setFillColor(...theme.primaryDark);
		doc.rect(0, 0, pageWidth, headerH, "F");

		// bottom band
		doc.setFillColor(...theme.primary);
		doc.rect(0, headerH - 20, pageWidth, 20, "F");

		// micro accent
		doc.setFillColor(...theme.accent);
		doc.rect(0, headerH - 3, pageWidth, 3, "F");

		// title
		doc.setFont("helvetica", "bold");
		doc.setFontSize(18);
		doc.setTextColor(255, 255, 255);
		doc.text(pageTitle, SP.pagePad, 44);

		// subtitle date
		doc.setFont("helvetica", "normal");
		doc.setFontSize(10);
		doc.setTextColor(235, 245, 255);
		doc.text(moment().format("DD MMMM YYYY"), SP.pagePad, 64);

		// watermark
		setOpacity(0.06);
		doc.setFont("helvetica", "bold");
		doc.setFontSize(40);
		doc.setTextColor(255, 255, 255);
		doc.text(landscape ? "RADAR" : "COMPARE", pageWidth - SP.pagePad, 56, {
			align: "right",
		});
		resetOpacity();

		doc.setTextColor(...theme.text);

		return headerH + 18; // start y
	};

	const drawSectionHeader = (title, {dotColor = theme.accent} = {}) => {
		const pageWidth = doc.internal.pageSize.getWidth();
		const x = SP.pagePad;
		const w = pageWidth - SP.pagePad * 2;

		doc.setFillColor(...theme.primary);
		doc.roundedRect(x, yPosition, w, 22, 10, 10, "F");

		doc.setFillColor(...dotColor);
		doc.circle(x + 12, yPosition + 11, 4, "F");

		doc.setFont("helvetica", "bold");
		doc.setFontSize(12);
		doc.setTextColor(255, 255, 255);
		doc.text(title, x + 22, yPosition + 15);

		yPosition += 22 + 10;
		doc.setTextColor(...theme.text);
	};

	const drawFooter = () => {
		const pageWidth = doc.internal.pageSize.getWidth();
		const pageHeight = doc.internal.pageSize.getHeight();

		const totalPages = doc.internal.pages.length - 1;
		const generatedAt = `Generato il ${moment().format("DD/MM/YYYY • HH:mm")}`;

		for (let i = 1; i <= totalPages; i++) {
			doc.setPage(i);

			doc.setDrawColor(...theme.border);
			doc.setLineWidth(1);
			doc.line(
				SP.pagePad,
				pageHeight - SP.footerH + 18,
				pageWidth - SP.pagePad,
				pageHeight - SP.footerH + 18,
			);

			doc.setFont("helvetica", "normal");
			doc.setFontSize(9);
			doc.setTextColor(...theme.muted);

			doc.text(generatedAt, SP.pagePad, pageHeight - 18);
			doc.text(
				`Pagina ${i} / ${totalPages}`,
				pageWidth - SP.pagePad,
				pageHeight - 18,
				{align: "right"},
			);
		}

		doc.setTextColor(...theme.text);
	};

	// =========================
	// Layout state (portrait)
	// =========================
	let yPosition = 0;

	const resetLayoutForCurrentPage = ({landscape = false} = {}) => {
		yPosition = drawHeader(
			t?.("comparison_report_title") || "COMPARAZIONE RADAR CHART GIOCATORI",
			{landscape},
		);
	};

	const checkPageBreak = (requiredHeight) => {
		const pageHeight = doc.internal.pageSize.getHeight();
		if (yPosition + requiredHeight > pageHeight - SP.footerH) {
			doc.addPage();
			resetLayoutForCurrentPage({landscape: false});
			return true;
		}
		return false;
	};

	// First page header
	resetLayoutForCurrentPage({landscape: false});

	// =========================
	// Players summary card (nice + compact)
	// =========================
	{
		const pageWidth = doc.internal.pageSize.getWidth();
		const x = SP.pagePad;
		const w = pageWidth - SP.pagePad * 2;

		// collect players
		const rows = (selectedPlayers || [])
			.map((p, idx) => {
				if (!p) return null;
				const playerData = playersData?.[p.value];
				const playerName = p.label?.split(" (")[0] || `Giocatore ${idx + 1}`;
				const category = p.label?.includes("(")
					? p.label.split("(")[1].replace(")", "")
					: playerData?.playerCategory?.name || "";
				return {idx, playerName, category};
			})
			.filter(Boolean);

		if (rows.length > 0) {
			checkPageBreak(86);

			drawSectionHeader("Giocatori Confrontati", {dotColor: theme.accent});

			const cardH = 64;
			drawCard(x, yPosition, w, cardH, {radius: 12, shadow: true});

			const colW = w - SP.cardPad * 2;
			const perRowH = 18;

			let startY = yPosition + SP.cardPad + 14;

			rows.slice(0, 3).forEach((r, i) => {
				// zebra
				if (i % 2 === 1) {
					doc.setFillColor(...theme.bg);
					doc.roundedRect(x + 8, startY - 12, w - 16, perRowH, 8, 8, "F");
				}

				// dot
				doc.setFillColor(...theme.accent);
				doc.circle(x + SP.cardPad, startY - 2, 3, "F");

				// label
				doc.setFont("helvetica", "bold");
				doc.setFontSize(10);
				doc.setTextColor(...theme.muted);
				doc.text(`Giocatore ${r.idx + 1}`, x + SP.cardPad + 10, startY);

				// value
				doc.setFont("helvetica", "normal");
				doc.setFontSize(11);
				doc.setTextColor(...theme.text);

				const rightText = `${r.playerName}${r.category ? ` (${r.category})` : ""}`;
				doc.text(
					ellipsize(rightText, colW - 110, 11),
					x + SP.cardPad + 90,
					startY,
				);

				startY += perRowH;
			});

			// if more than 3, small note
			if (rows.length > 3) {
				doc.setFont("helvetica", "italic");
				doc.setFontSize(9);
				doc.setTextColor(...theme.muted);
				doc.text(
					`+${rows.length - 3} altri giocatori selezionati`,
					x + SP.cardPad,
					yPosition + cardH - 10,
				);
			}

			yPosition += cardH + SP.blockGap;
		}
	}

	// =========================
	// Charts capture helpers
	// =========================
	const captureChartToCanvas = async (chartElement, {scale = 2} = {}) => {
		// wrapper: background + scale + correct size
		const canvas = await html2canvas(chartElement, {
			backgroundColor: "#ffffff",
			scale,
			useCORS: true,
			logging: false,
			width: chartElement.scrollWidth,
			height: chartElement.scrollHeight,
		});
		return canvas;
	};

	const addChartCard = async ({
		chartElement,
		title,
		pageMode = "portrait", // portrait | landscape
		captureScale = 2,
		minCardH = 220,
	}) => {
		if (!chartElement) return;

		const isLandscape = pageMode === "landscape";

		// If landscape requested: always create a fresh landscape page for clarity
		if (isLandscape) {
			doc.addPage("a4", "landscape");
			resetLayoutForCurrentPage({landscape: true});
		}

		const pageWidth = doc.internal.pageSize.getWidth();
		const pageHeight = doc.internal.pageSize.getHeight();
		const x = SP.pagePad;
		const w = pageWidth - SP.pagePad * 2;

		// Title
		checkPageBreak(48);
		drawSectionHeader(title, {dotColor: theme.primaryLight});

		// Capture chart
		try {
			const canvas = await captureChartToCanvas(chartElement, {
				scale: captureScale,
			});
			const imgData = canvas.toDataURL("image/png");

			// Compute fit (keep ratio)
			const padding = SP.cardPad;
			const headerInside = 0;

			const availableW = w - padding * 2;
			const availableH =
				pageHeight - SP.footerH - yPosition - padding * 2 - headerInside;

			const aspect = canvas.width / canvas.height;

			let imgW = availableW;
			let imgH = imgW / aspect;

			if (imgH > availableH) {
				imgH = availableH;
				imgW = imgH * aspect;
			}

			const cardH = Math.max(minCardH, imgH + padding * 2);

			checkPageBreak(cardH + SP.blockGap);

			drawCard(x, yPosition, w, cardH, {radius: 12, shadow: true});

			// Center image inside card
			const imgX = x + (w - imgW) / 2;
			const imgY = yPosition + (cardH - imgH) / 2;

			doc.addImage(imgData, "PNG", imgX, imgY, imgW, imgH);

			yPosition += cardH + SP.blockGap;
		} catch (error) {
			console.error("Errore nel catturare il grafico:", error);

			const fallbackH = 90;
			checkPageBreak(fallbackH + SP.blockGap);
			drawCard(x, yPosition, w, fallbackH, {radius: 12, shadow: true});

			doc.setFont("helvetica", "italic");
			doc.setFontSize(10);
			doc.setTextColor(...theme.muted);
			doc.text("Grafico non disponibile", x + SP.cardPad, yPosition + 40);

			yPosition += fallbackH + SP.blockGap;
			doc.setTextColor(...theme.text);
		}
	};

	// =========================
	// Public API (same usage)
	// =========================
	return {
		addIndividualChart: async (chartElement, playerIndex) => {
			const player = selectedPlayers?.[playerIndex];
			if (!player) return;

			const playerName =
				player.label?.split(" (")[0] || `Giocatore ${playerIndex + 1}`;

			await addChartCard({
				chartElement,
				title: `Radar Chart — ${playerName}`,
				pageMode: "portrait",
				captureScale: 2, // good quality, not too heavy
				minCardH: 240,
			});
		},

		addCombinedChart: async (chartElement) => {
			await addChartCard({
				chartElement,
				title: "Radar Chart Comparativo",
				pageMode: "landscape",
				captureScale: 4, // max readability like your version
				minCardH: 0, // in landscape we fit page anyway
			});
		},

		save: (
			filename = `Comparazione_Giocatori_${moment().format("YYYY-MM-DD")}.pdf`,
		) => {
			drawFooter();
			doc.save(filename);
		},
	};
};
